import type { kanbanCopy } from '../pt/kanban';

/**
 * Español (neutro, LatAm) — módulo Kanban.
 *
 * Sem "vosotros": o imperativo é sempre a forma de "tú" ("Mueve", "Completa",
 * "Arrastra"), que serve México, Colômbia e Cone Sul sem soar peninsular.
 *
 * "Cycle Time", "Throughput", "Burndown", "Backlog", "Sprint" e "Label" ficam
 * em inglês: é como o mercado ágil hispanofalante escreve e como aparecem na
 * literatura de Kanban/Scrum. Traduzi-los quebraria o reconhecimento.
 */
const kanban: kanbanCopy = {
  module: {
    title: 'Kanban',
    taskSingular: 'tarea',
    taskPlural: 'tareas',
    emptyTitle: 'Ninguna tarea en el proyecto',
    emptyBody: 'Agrega tareas en el Gantt o crea tarjetas directamente en las columnas del Kanban.',
    moveError: 'Error al guardar el movimiento. Intenta de nuevo.',
    reorderError: 'Error al reordenar. Intenta de nuevo.',

    actions: {
      publish: 'Publicar',
      metrics: 'Métricas',
      sprints: 'Sprints',
      labels: 'Labels',
      columns: 'Columnas',
    },

    filters: {
      sprintPlaceholder: 'Sprint',
      sprintAll: 'Todos',
      sprintBacklog: 'Backlog (sin sprint)',
      assigneePlaceholder: 'Responsable',
      assigneeAll: 'Todos',
      priorityPlaceholder: 'Prioridad',
      priorityAll: 'Todas',
      groupPlaceholder: 'Agrupar',
      /* Tira do celular quando o projeto esta agrupado: o modo raia nao
       * cabe no telefone, e sumir sem dizer nada deixaria a pessoa achando
       * que o agrupamento se perdeu. */
      semRaiaNoCelular: 'Agrupado por {{grupo}} en el ordenador. Aquí el tablero muestra todo en columnas.',
      desagrupar: 'Desagrupar',
      sheetTitle: 'Filtros',
      sheetGroupFilters: 'Filtrar por',
      sheetGroupView: 'Visualización',
      sheetClear: 'Limpiar',
      sheetApply: 'Ver {{count}} tareas',
      sheetApplyOne: 'Ver 1 tarea',
    },

    priority: {
      critical: 'Crítica',
      high: 'Alta',
      medium: 'Media',
      none: 'Ninguna',
    },

    groupBy: {
      none: 'Sin agrupar',
      sprint: 'Por Sprint',
      assignee: 'Por Responsable',
      priority: 'Por Prioridad',
    },

    swimlane: {
      sprintActive: 'Activo',
      sprintCompleted: 'Completado',
      sprintPlanning: 'Planificación',
      backlog: 'Backlog',
      backlogSublabel: 'Sin sprint',
      unassigned: 'Sin Asignar',
    },
  },

  card: {
    priority: {
      medium: 'Media',
      high: 'Alta',
      critical: 'Crítica',
    },
    priorityTitle: 'Prioridad: {{level}}',
    milestone: 'Hito',
    blocked: 'Bloqueado',
    overdue: 'Atrasada',
    dueSoon: 'Vence pronto',
    dueDate: 'Fecha de entrega',
    dependencies: '{{count}} dependencia(s)',
    attachments: '{{count}} adjunto(s)',
    comments: '{{count}} comentario(s)',
    checklistBadge: 'Checklist: {{done}} de {{total}}',
  },

  column: {
    dropHint: 'Arrastra tarjetas aquí',
    newCardPlaceholder: 'Nombre de la tarea...',
    addCard: 'Agregar tarea',
    add: 'Agregar',
    cancel: 'Cancelar',
    useTemplate: 'Crear desde plantilla',
    addColumn: 'Nueva columna',
    newColumnPlaceholder: 'Nombre de la columna...',
    newColumnCategory: 'Entra como {{categoria}}',
  },

  swimlane: {
    empty: 'Vacío',
    newCardPlaceholder: 'Nombre de la tarea...',
    add: 'Agregar',
    cancel: 'Cancelar',
    addCard: 'Tarjeta',
  },

  categories: {
    not_started: 'Por hacer',
    in_progress: 'En curso',
    on_hold: 'Detenida',
    completed: 'Completada',
    hint: {
      not_started: 'Reinicia el progreso. El Portafolio la cuenta como no iniciada.',
      in_progress: 'Pone el progreso en 5% cuando la tarjeta está en cero.',
      on_hold: 'No toca el progreso. Aquí viven "Pausada" y "Bloqueada".',
      completed: 'Cierra el progreso en 100%. Es lo que el Portafolio cuenta como entregado.',
    },
  },

  columnSettings: {
    title: 'Columnas del tablero',
    subtitle: 'La columna dice dónde está la tarjeta. La categoría dice qué significa en el Portafolio.',
    newColumnName: 'Nueva columna',
    namePlaceholder: 'Nombre de la columna',
    category: 'Categoría',
    addColumn: 'Agregar columna',
    cancel: 'Cancelar',
    save: 'Guardar',
    moveUp: 'Mover hacia arriba',
    moveDown: 'Mover hacia abajo',
    remove: 'Eliminar columna',
    deleteTitle: 'Eliminar la columna "{{name}}"',
    deleteWithCards: {
      one: 'Tiene {{count}} tarjeta. Elige a dónde va.',
      other: 'Tiene {{count}} tarjetas. Elige a dónde van.',
    },
    deleteEmpty: 'La columna está vacía.',
    deleteTarget: 'Mover las tarjetas a',
    deleteConfirm: 'Eliminar columna',
    needsDone: 'El tablero necesita al menos una columna en la categoría Completada.',
    needsTodo: 'El tablero necesita al menos una columna en la categoría Por hacer.',
    needsName: 'Toda columna necesita un nombre.',
    saveError: 'No se pudieron guardar las columnas. Inténtalo de nuevo.',
    cardCount: {
      zero: 'Ninguna tarjeta en esta columna',
      one: '{{count}} tarjeta en esta columna',
      other: '{{count}} tarjetas en esta columna',
    },
  },

  labelSettings: {
    title: 'Gestionar Labels',
    empty: 'Ningún label creado.',
    newLabelName: 'Nuevo Label',
    namePlaceholder: 'Nombre del label',
    addLabel: 'Agregar Label',
    cancel: 'Cancelar',
    save: 'Guardar',
  },

  metrics: {
    title: 'Métricas del Kanban',

    tabs: {
      overview: 'Visión General',
      cycletime: 'Cycle Time',
      flow: 'Flujo Acumulado',
      throughput: 'Throughput',
      burndown: 'Burndown',
    },

    periods: {
      d7: '7 días',
      d14: '14 días',
      d30: '30 días',
    },

    avgCycleTime: 'Cycle Time Promedio',
    medianCycleTime: 'Cycle Time Mediano',
    completedTasks: 'Tareas Completadas',
    lastDays: 'en los últimos {{days}} días',
    avgThroughput: 'Throughput Promedio',
    perWeek: '{{value}}/sem',
    tasksPerWeek: 'tareas por semana',
    emptyOverview: 'Sin historial suficiente. Mueve tareas entre columnas para generar métricas.',

    axisDays: 'Días',
    tooltipDays: '{{value}} días',
    tooltipCycleTime: 'Cycle Time',
    emptyCycleTime: 'Sin datos de cycle time. Mueve tareas hasta la columna final para generar datos.',

    emptyFlow: 'Sin datos de flujo acumulado.',

    axisWeekOf: 'Semana del',
    tooltipTasks: '{{value}} tareas',
    seriesCompleted: 'Completadas',
    emptyThroughput: 'Sin datos de throughput. Completa tareas para generar datos.',

    sprintLabel: 'Sprint:',
    seriesIdeal: 'Ideal',
    seriesRemaining: 'Restante',
    emptyBurndown: 'Sin datos de burndown para el sprint activo.',
  },

  sprints: {
    title: 'Gestionar Sprints',
    empty: 'Ningún sprint creado. Crea tu primer sprint para organizar las tareas.',

    status: {
      planning: 'Planificación',
      active: 'Activo',
      completed: 'Completado',
      canceled: 'Cancelado',
    },

    namePlaceholder: 'Nombre del sprint',
    goalPlaceholder: 'Objetivo del sprint',
    goalPlaceholderNew: 'Objetivo del sprint (opcional)',
    startLabel: 'Inicio',
    endLabel: 'Fin',
    startPlaceholder: 'Inicio',
    endPlaceholder: 'Fin',
    start: 'Iniciar Sprint',
    complete: 'Completar Sprint',
    delete: 'Eliminar Sprint',
    deleteConfirm: '¿Eliminar este sprint? Las tareas vuelven al backlog.',
    create: 'Crear',
    newSprint: 'Nuevo Sprint',
    cancel: 'Cancelar',
    save: 'Guardar',
    close: 'Cerrar',
  },

  detail: {
    overEstimate: 'por encima de lo estimado',
    groupExecution: 'Ejecución',
    groupSchedule: 'Cronograma',
    groupEffort: 'Esfuerzo',
    useTemplate: 'Usar plantilla',
    predecessorsAdd: 'Añadir',
    templates: {
      user_story: {
        label: 'Historia de Usuario',
        html: '<h2>Historia de Usuario</h2><p><strong>Como</strong> [tipo de usuario],</p><p><strong>Quiero</strong> [acción/funcionalidad],</p><p><strong>Para</strong> [beneficio/valor].</p><h2>Criterios de Aceptación</h2><ul><li>[ ] </li><li>[ ] </li></ul><h2>Notas</h2><p></p>',
      },
      bug_report: {
        label: 'Reporte de Bug',
        html: '<h2>Descripción del Bug</h2><p></p><h2>Pasos para Reproducir</h2><ol><li></li><li></li><li></li></ol><h2>Comportamiento Esperado</h2><p></p><h2>Comportamiento Actual</h2><p></p><h2>Entorno</h2><ul><li>Navegador: </li><li>Sistema: </li></ul>',
      },
      spike: {
        label: 'Spike / Investigación',
        html: '<h2>Objetivo</h2><p></p><h2>Preguntas a Responder</h2><ul><li></li><li></li></ul><h2>Restricciones</h2><p>Timebox: </p><h2>Resultado Esperado</h2><p></p>',
      },
      task: {
        label: 'Tarea Técnica',
        html: '<h2>Contexto</h2><p></p><h2>Qué hacer</h2><ul><li></li></ul><h2>Definition of Done</h2><ul><li>[ ] Código implementado</li><li>[ ] Pruebas pasando</li><li>[ ] Code review aprobado</li></ul>',
      },
    },

    priority: {
      none: 'Ninguna',
      medium: 'Media',
      high: 'Alta',
      critical: 'Crítica',
    },

    undoField: 'Cambiar {{field}}',

    namePlaceholder: 'Nombre de la tarea',
    prevTask: 'Tarea anterior (←)',
    nextTask: 'Tarea siguiente (→)',
    saving: 'Guardando...',
    saved: 'Guardado',

    description: 'Descripción',
    templateLabel: 'Plantilla:',
    templateReplaceTitle: '¿Reemplazar la descripción?',
    templateReplaceMessage: 'La descripción actual será reemplazada por la plantilla. Puedes deshacer con Ctrl+Z en el editor.',
    templateReplaceConfirm: 'Reemplazar',

    templateApplyTitle: 'Aplicar plantilla',
    templateApplyMessage: 'La descripción y el checklist actuales serán reemplazados por el contenido de la plantilla. Prioridad, estimación y etiquetas también se aplican.',
    templateApplyConfirm: 'Aplicar',
    saveAsTemplate: 'Guardar como plantilla',
    templateSaveTitle: 'Guardar como plantilla',
    templateSaveNamePlaceholder: 'Nombre de la plantilla',
    templateSaveHint: 'Incluye descripción, checklist, etiquetas, prioridad y estimación. Responsables y fechas quedan fuera.',
    templateSaveCancel: 'Cancelar',
    templateSaveConfirm: 'Guardar',
    templateSaved: 'Plantilla guardada',
    templateSaveError: 'No se pudo guardar la plantilla',
    cardActions: 'Acciones de la tarjeta',
    copyLink: 'Copiar enlace',
    linkCopied: 'Enlace copiado',
    linkCopyError: 'No se pudo copiar el enlace',
    duplicateCard: 'Duplicar tarjeta',
    deleteCard: 'Eliminar tarjeta',
    duplicated: 'Tarjeta duplicada',
    deleteConfirmTitle: 'Eliminar tarjeta',
    deleteConfirmMessage: '¿Eliminar esta tarea? Las subtareas también serán eliminadas. Esta acción no se puede deshacer.',
    deleteConfirmLabel: 'Eliminar',
    descriptionPlaceholder: 'Describe la tarea, user story, criterios de aceptación... Usa @ para mencionar miembros',

    attachments: {
      title: 'Adjuntos',
      empty: 'Sin adjuntos. Arrastra un archivo aquí o pega un enlace.',
      addFile: 'Adjuntar archivo',
      addLink: 'Añadir enlace',
      linkPlaceholder: 'https://...',
      linkInvalid: 'Dirección inválida. Usa http, https o mailto.',
      dropHere: 'Suelta para adjuntar',
      uploading: 'Subiendo...',
      download: 'Descargar',
      open: 'Abrir',
      remove: 'Quitar',
      removeConfirm: '¿Quitar este adjunto?',
      by: 'por {{name}}',
      mimeNotAllowed: 'Tipo de archivo no aceptado. Sube PDF, Word, Excel, PowerPoint, texto, imagen o ZIP.',
      quota: {
        usage: '{{used}} de {{limit}} en archivos adjuntos',
        nearLimit: 'Queda {{left}} de espacio para adjuntos.',
        upgradeHint: 'En {{plan}} son {{planLimit}}.',
        seePlans: 'Ver planes',
        blockedTitle: 'Espacio de adjuntos agotado',
        blockedBody: 'Ya usaste {{used}} de los {{limit}} del plan {{currentPlan}}. Libera espacio quitando adjuntos antiguos, o cambia de plan.',
        fileTooLargeTitle: 'Archivo demasiado grande',
        fileTooLargeBody: 'Este archivo pesa {{size}} y el límite por archivo en el plan {{currentPlan}} es {{limit}}. Comprime el archivo, o cambia de plan.',
        close: 'Cerrar',
      },
    },

    checklist: 'Checklist',
    checklistPlaceholder: 'Agregar ítem...',
    checklistAdd: 'Agregar',

    subtasks: 'Subtareas',
    subtaskAdd: 'Agregar',
    subtasksEmpty: 'Sin subtareas. Las subtareas aparecen en el Gantt como jerarquía.',
    newSubtask: 'Nueva subtarea',

    status: 'Estado',
    statusPlaceholder: 'Seleccionar',
    autoBadge: 'Auto',

    sprint: 'Sprint',
    backlog: 'Backlog',

    startLabel: 'Inicio',
    startPlaceholder: 'Seleccionar inicio',
    endLabel: 'Fin',
    endPlaceholder: 'Seleccionar fin',
    durationLabel: 'Duración',
    durationPlaceholder: 'ej: 5d, 16h, 2w',
    durationError: 'Formato: 5d, 16h, 2w, 1m',

    progress: 'Progreso',
    progressAria: 'Progreso de la tarea',

    estimated: 'Estimado',
    actual: 'Real',
    hoursPlaceholder: '0h',

    predecessors: 'Predecesoras',
    predecessorsEmpty: 'Ninguna',
    edit: 'Editar',

    assignees: 'Responsables',
    priorityLabel: 'Prioridad',
    labels: 'Labels',

    blocked: 'Bloqueado',
    blockedAria: 'Tarea bloqueada',
    yes: 'Sí',
    no: 'No',
  },
};

export default kanban;
